import { format, isValid } from "date-fns";

import { parseApiDate } from "../../../../helpers";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// GROWTH-7578: how long a card advertises its age. Under a week it reads as news ("3h ago"); after
// that the card says why it is in the feed instead, because a listing's age is only good news for
// something that expires.
const FRESH_DAYS = 7;
const UPDATED_DATE_DAYS = 60;
const JOB_POSTED_DAYS = 30;

const REASON_KEYS = {
  practice_match: "dashboard_feed_reason_practice_match",
  near_you: "dashboard_feed_reason_near_you",
  popular: "dashboard_feed_reason_popular",
  community_default: "dashboard_feed_reason_community_default"
};

const time = value => {
  const date = value ? parseApiDate(value) : null;
  return date && isValid(date) ? date : null;
};

// "Just now", "30m ago", "3h ago", "1 day ago", "5 days ago". Only called under FRESH_DAYS, so it
// never produces weeks, months or years.
const relative = (ageMs, t) => {
  if (ageMs < MINUTE_MS) return t("dashboard_feed_time_just_now");
  if (ageMs < HOUR_MS) return t("dashboard_feed_time_minutes_ago", { count: Math.floor(ageMs / MINUTE_MS) });
  if (ageMs < DAY_MS) return t("dashboard_feed_time_hours_ago", { count: Math.floor(ageMs / HOUR_MS) });
  return t("dashboard_feed_time_days_ago", { count: Math.floor(ageMs / DAY_MS) });
};

const reasonText = (item, t, { city, state }) => {
  const community = item.community_name;
  const { reason } = item;

  // Anything else, a missing or unknown reason included, is the default one.
  if (reason === "near_you" && city && state) return t(REASON_KEYS.near_you, { city, state });
  if (reason === "popular" && community) return t(REASON_KEYS.popular, { community });
  if (reason === "practice_match") return t(REASON_KEYS.practice_match);
  return community ? t(REASON_KEYS.community_default, { community }) : null;
};

/**
 * The attribution line's time slot (`Community · Type · ⟨slot⟩ · New`), GROWTH-7578.
 *
 * touched = item.touched_at (the latest of created, edited, approved; created_at for a payload
 * without it) and age = now - touched.
 * - Under 7 days: relative time, prefixed "Updated " when touched is not created_at and "Posted " for a job.
 * - From 7 days: never a relative time. "Updated MMM d" when touched is not created_at and under 60
 *   days; else, for a job under 30 days, "Posted MMM d"; else the reason the item is in the feed. An event
 *   shows only a known reason (its date is already on the card), else nothing.
 *
 * @param {Object} item one FeedItem
 * @param {Function} t i18next translate function
 * @param {Object} options
 * @param {Date} options.now
 * @param {string} [options.city] the member's profile city, for the near_you reason
 * @param {string} [options.state] the member's profile state, for the near_you reason
 * @param {boolean} [options.showReason=true] false on the Saved page, whose cards are not in the feed
 *   for a reason: from 7 days the slot is empty
 * @returns {string|null} null when there is nothing to show
 */
export const whenFor = (item, t, { now, city, state, showReason = true }) => {
  const created = time(item.created_at);
  const touched = time(item.touched_at) || created;
  if (!touched) return null;

  const ageMs = Math.max(0, now.getTime() - touched.getTime());
  const ageDays = ageMs / DAY_MS;
  const updated = Boolean(created) && touched.getTime() !== created.getTime();

  if (ageDays < FRESH_DAYS) {
    const when = relative(ageMs, t);
    if (updated) return t("dashboard_feed_time_updated", { when });
    return item.type === "job" ? t("dashboard_feed_time_posted", { when }) : when;
  }

  const date = format(touched, "MMM d");
  if (updated && ageDays < UPDATED_DATE_DAYS) return t("dashboard_feed_time_updated_on", { date });
  if (item.type === "job" && ageDays < JOB_POSTED_DAYS) return t("dashboard_feed_time_posted_on", { date });

  if (!showReason) return null;
  if (item.type === "event" && !REASON_KEYS[item.reason]) return null;
  return reasonText(item, t, { city, state });
};
